/**
 * Smith Protective — Dedupe modal (Iter196).
 *
 * Reusable modal that runs the two dedupe operations:
 *   • Contacts — collapses duplicate decision-maker emails inside each prospect
 *   • Prospects — merges duplicate prospects (same domain OR name+city) into
 *     the survivor with higher opportunity_score, archives the loser
 *
 * Both modes have a dry-run scan first, showing what WILL happen; the operator
 * confirms with an explicit apply click. super_admin only for apply; scans
 * work for master_demo too.
 *
 * Props:
 *   • open (bool)
 *   • onClose ()
 *   • onApplied (result) — optional, fires after successful apply so parents
 *     can refresh their lists
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Trash2, Users, Building2, Loader2, CheckCircle2, AlertTriangle,
  Play, RefreshCw, Archive, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const friendlyError = (e, fb = "Falha") => {
  const s = e?.response?.status;
  const d = e?.response?.data;
  if (d?.detail) return d.detail;
  if (d?.error?.message) return d.error.message;
  if (s === 502 || s === 503 || s === 504) return "Servidor demorou — tente de novo em 30s.";
  return e?.message || fb;
};

export default function DedupeModal({ open, onClose, onApplied }) {
  const [mode, setMode] = useState("contacts");    // 'contacts' | 'prospects'
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState({ contacts: null, prospects: null });
  const [applyResult, setApplyResult] = useState(null);

  const runScan = async (which) => {
    setBusy(true);
    setApplyResult(null);
    try {
      const url = which === "contacts"
        ? `${API}/smith/dedupe/scan-contacts?limit=2000`
        : `${API}/smith/dedupe/scan-prospects?limit=5000`;
      const { data } = await axios.get(url);
      setScan((cur) => ({ ...cur, [which]: data }));
    } catch (e) {
      toast.error(friendlyError(e, "Falha no scan"));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!open) return;
    // Auto-scan whichever mode is currently active on open + mode changes
    if (!scan[mode]) runScan(mode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const applyDedupe = async () => {
    const label = mode === "contacts"
      ? "Colapsar emails duplicados dentro dos prospects?"
      : "Arquivar prospects duplicados (movidos para archive — NÃO deletados)?";
    if (!confirm(label)) return;
    setBusy(true);
    setApplyResult(null);
    try {
      const url = mode === "contacts"
        ? `${API}/smith/dedupe/apply-contacts`
        : `${API}/smith/dedupe/apply-prospects`;
      const { data } = await axios.post(url, {});
      setApplyResult(data);
      const msg = mode === "contacts"
        ? `${data.total_dupes_removed || 0} emails duplicados removidos em ${data.prospects_touched} prospects`
        : `${data.prospects_archived || 0} prospects arquivados · ${data.decision_makers_folded || 0} DMs fundidos`;
      toast.success(msg);
      // Re-scan to show empty state confirming
      await runScan(mode);
      if (onApplied) onApplied(data);
    } catch (e) {
      toast.error(friendlyError(e, "Falha ao aplicar dedupe"));
    } finally { setBusy(false); }
  };

  if (!open) return null;

  const current = scan[mode];
  const contactsCount = scan.contacts?.total_dupes_removed || 0;
  const prospectsCount = scan.prospects?.prospects_to_archive || 0;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         onClick={onClose}
         data-testid="dedupe-modal">
      <div className="w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-2xl border flex flex-col"
           style={{ background: "#0B1120", borderColor: "rgba(139,92,246,0.35)" }}
           onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
               style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#5B21B6 100%)" }}>
            <Trash2 className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-[14px] font-extrabold text-white">Limpar duplicatas</div>
            <div className="text-[11px] text-slate-400">
              Remove emails repetidos entre decision-makers e funde prospects duplicados.
              Nenhum dado é deletado — só arquivado.
            </div>
          </div>
          <button onClick={onClose}
                  data-testid="dedupe-modal-close"
                  className="w-8 h-8 rounded hover:bg-white/5 text-slate-400 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Mode tabs */}
        <div className="px-5 pt-3 border-b border-white/10 flex gap-1">
          <button type="button" onClick={() => setMode("contacts")}
                  data-testid="dedupe-tab-contacts"
                  className={`px-4 py-2 text-[12px] font-extrabold border-b-2 -mb-px inline-flex items-center gap-2 ${mode === "contacts" ? "border-[#8B5CF6] text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
            <Users className="w-3.5 h-3.5" />
            Emails dentro de contatos
            {contactsCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5CF6]/25 text-[#C4B5FD]">{contactsCount}</span>}
          </button>
          <button type="button" onClick={() => setMode("prospects")}
                  data-testid="dedupe-tab-prospects"
                  className={`px-4 py-2 text-[12px] font-extrabold border-b-2 -mb-px inline-flex items-center gap-2 ${mode === "prospects" ? "border-[#8B5CF6] text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
            <Building2 className="w-3.5 h-3.5" />
            Empresas duplicadas
            {prospectsCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B5CF6]/25 text-[#C4B5FD]">{prospectsCount}</span>}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5" data-testid={`dedupe-content-${mode}`}>
          {busy && !current ? (
            <div className="py-12 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#8B5CF6]" />
            </div>
          ) : mode === "contacts" ? (
            <ContactsView scan={current} onApply={applyDedupe} onRescan={() => runScan("contacts")}
                          busy={busy} applyResult={applyResult} />
          ) : (
            <ProspectsView scan={current} onApply={applyDedupe} onRescan={() => runScan("prospects")}
                           busy={busy} applyResult={applyResult} />
          )}
        </div>
      </div>
    </div>
  );
}

function ContactsView({ scan, onApply, onRescan, busy, applyResult }) {
  const touched = scan?.prospects_touched || 0;
  const total = scan?.total_dupes_removed || 0;
  const plans = scan?.plans || [];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border p-4 flex items-center gap-3"
           style={{ borderColor: total > 0 ? "rgba(245,158,11,0.4)" : "rgba(16,185,129,0.4)",
                     background: total > 0 ? "rgba(245,158,11,0.06)" : "rgba(16,185,129,0.06)" }}>
        <div className="flex-1">
          <div className="text-[13px] font-extrabold text-white">
            {total > 0
              ? <>Encontramos <span className="text-[#F59E0B]">{total} emails duplicados</span> em <span className="text-[#F59E0B]">{touched} prospects</span></>
              : "Nenhuma duplicata encontrada nos contatos"}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            Merge preserva o registro mais completo (com mais campos preenchidos) e funde phone/linkedin dos outros.
          </div>
        </div>
        <button type="button" onClick={onRescan} disabled={busy}
                data-testid="dedupe-rescan-contacts"
                className="h-8 px-3 rounded text-[11px] font-bold text-slate-300 border border-white/15 hover:border-white/30 disabled:opacity-50 inline-flex items-center gap-1.5">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Re-scan
        </button>
        <button type="button" onClick={onApply} disabled={busy || total === 0}
                data-testid="dedupe-apply-contacts"
                className="h-8 px-4 rounded text-[11.5px] font-extrabold text-white disabled:opacity-30 inline-flex items-center gap-1.5"
                style={{ background: "linear-gradient(135deg,#8B5CF6 0%,#5B21B6 100%)" }}>
          <Play className="w-3 h-3" /> Aplicar dedupe
        </button>
      </div>

      {applyResult && (
        <div className="rounded-lg border p-3 text-[12px] flex items-center gap-2 text-emerald-300"
             style={{ borderColor: "rgba(16,185,129,0.4)", background: "rgba(16,185,129,0.08)" }}
             data-testid="dedupe-apply-result-contacts">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Dedupe aplicado · {applyResult.prospects_touched} prospects atualizados · {applyResult.total_dupes_removed} emails removidos
        </div>
      )}

      {plans.length > 0 && (
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <header className="px-3 py-2 bg-black/30 border-b border-white/10">
            <span className="text-[11px] font-extrabold text-slate-300 uppercase tracking-widest">
              Prospects afetados ({plans.length})
            </span>
          </header>
          <div className="max-h-[45vh] overflow-y-auto divide-y divide-white/5"
               data-testid="dedupe-contacts-plans">
            {plans.slice(0, 100).map((p, i) => (
              <div key={p.prospect_id} className="px-3 py-2"
                   data-testid={`dedupe-plan-contact-${i}`}>
                <div className="flex items-center gap-2">
                  <Users className="w-3 h-3 text-[#8B5CF6]" />
                  <span className="text-[12px] font-bold text-white truncate flex-1">
                    {p.company_name || p.prospect_id}
                  </span>
                  <span className="text-[10.5px] text-slate-400">
                    {p.before_count} → <b className="text-[#F59E0B]">{p.after_count}</b>
                    <span className="ml-1 text-emerald-400">(-{p.removed_count})</span>
                  </span>
                </div>
                <div className="pl-5 mt-1 space-y-0.5">
                  {Object.entries(p.dupe_groups || {}).slice(0, 3).map(([email, rows]) => (
                    <div key={email} className="text-[10px] text-slate-500 truncate">
                      <span className="font-mono text-slate-400">{email}</span> — {rows.length} rows
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProspectsView({ scan, onApply, onRescan, busy, applyResult }) {
  const groups = scan?.groups || [];
  const archiveCount = scan?.prospects_to_archive || 0;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border p-4 flex items-center gap-3"
           style={{ borderColor: archiveCount > 0 ? "rgba(245,158,11,0.4)" : "rgba(16,185,129,0.4)",
                     background: archiveCount > 0 ? "rgba(245,158,11,0.06)" : "rgba(16,185,129,0.06)" }}>
        <div className="flex-1">
          <div className="text-[13px] font-extrabold text-white">
            {archiveCount > 0
              ? <>Encontramos <span className="text-[#F59E0B]">{scan.groups_found} grupos</span> com <span className="text-[#F59E0B]">{archiveCount} prospects duplicados</span></>
              : "Nenhum prospect duplicado encontrado"}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            Sobrevivente: maior <b>opportunity_score</b>. Losers ficam <b>arquivados</b> (não deletados) — pode reverter em `merged_prospects[]`.
          </div>
        </div>
        <button type="button" onClick={onRescan} disabled={busy}
                data-testid="dedupe-rescan-prospects"
                className="h-8 px-3 rounded text-[11px] font-bold text-slate-300 border border-white/15 hover:border-white/30 disabled:opacity-50 inline-flex items-center gap-1.5">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Re-scan
        </button>
        <button type="button" onClick={onApply} disabled={busy || archiveCount === 0}
                data-testid="dedupe-apply-prospects"
                className="h-8 px-4 rounded text-[11.5px] font-extrabold text-white disabled:opacity-30 inline-flex items-center gap-1.5"
                style={{ background: "linear-gradient(135deg,#EF4444 0%,#7F1D1D 100%)" }}>
          <Archive className="w-3 h-3" /> Arquivar duplicatas
        </button>
      </div>

      <div className="rounded-lg border border-white/10 p-3 flex items-start gap-2 bg-black/20"
           data-testid="dedupe-prospects-info">
        <Info className="w-3.5 h-3.5 text-[#F59E0B] shrink-0 mt-0.5" />
        <div className="text-[11px] text-slate-400 leading-relaxed">
          <b className="text-slate-200">Como agrupamos:</b> prospects com o mesmo domínio de website (ex: <span className="font-mono">acme.com</span>) OU mesmo nome canonicalizado + cidade. Sufixos legais (LLC, Inc., Corp) são ignorados no match.
        </div>
      </div>

      {applyResult && (
        <div className="rounded-lg border p-3 text-[12px] flex items-center gap-2 text-emerald-300"
             style={{ borderColor: "rgba(16,185,129,0.4)", background: "rgba(16,185,129,0.08)" }}
             data-testid="dedupe-apply-result-prospects">
          <CheckCircle2 className="w-3.5 h-3.5" />
          Dedupe aplicado · {applyResult.prospects_archived} arquivados · {applyResult.decision_makers_folded} DMs fundidos no sobrevivente
        </div>
      )}

      {groups.length > 0 && (
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <header className="px-3 py-2 bg-black/30 border-b border-white/10">
            <span className="text-[11px] font-extrabold text-slate-300 uppercase tracking-widest">
              Grupos ({groups.length})
            </span>
          </header>
          <div className="max-h-[45vh] overflow-y-auto divide-y divide-white/5"
               data-testid="dedupe-prospects-groups">
            {groups.slice(0, 100).map((g, i) => (
              <div key={g.survivor_id + i} className="px-3 py-2 space-y-1"
                   data-testid={`dedupe-group-${i}`}>
                <div className="flex items-center gap-2">
                  <Building2 className="w-3 h-3 text-emerald-400" />
                  <span className="text-[12px] font-extrabold text-white truncate flex-1">
                    {g.survivor_name || g.survivor_id}
                  </span>
                  <span className="text-[10px] text-emerald-400 shrink-0">
                    ✓ survivor · score {g.survivor_score}
                  </span>
                </div>
                <div className="pl-5 space-y-0.5">
                  {(g.losers || []).map((l, j) => (
                    <div key={l.prospect_id + j} className="flex items-center gap-2 text-[10.5px] text-slate-400">
                      <AlertTriangle className="w-2.5 h-2.5 text-[#F59E0B]" />
                      <span className="truncate flex-1">{l.name} <span className="text-slate-500">({l.city}/{l.state})</span></span>
                      <span className="tabular-nums text-slate-500">score {l.score} · {l.dms} DMs</span>
                    </div>
                  ))}
                </div>
                <div className="pl-5 text-[9.5px] text-slate-500 font-mono truncate">{g.signal}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
